import { silentFetch } from './apiClient';
import db from './db';

const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
let syncTimer = null;

export const syncService = {
  status: 'idle', // idle | syncing | error
  listeners: new Set(),

  onStatusChange(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  },

  setStatus(status) {
    this.status = status;
    this.listeners.forEach(fn => fn(status));
  },

  startPeriodicSync() {
    this.stopPeriodicSync();
    syncTimer = setInterval(() => this.syncAll(), SYNC_INTERVAL);
  },

  stopPeriodicSync() {
    if (syncTimer) {
      clearInterval(syncTimer);
      syncTimer = null;
    }
  },

  async syncAll() {
    if (this.status === 'syncing') return;
    this.setStatus('syncing');

    try {
      await this.pushDirtyDecks();
      await this.pushDirtyCards();
      await this.pushDirtyReviews();
      await this.pushDirtyStats();
      this.setStatus('idle');
    } catch (err) {
      console.error('Sync failed:', err);
      this.setStatus('error');
    }
  },

  async initialSync() {
    if (this.status === 'syncing') return;
    this.setStatus('syncing');

    try {
      // Pull remote decks via silentFetch — transient network failures during
      // app boot (token not yet restored, offline, CORS race) must NOT toast
      // "Network error" at the user; the offline-first store carries them.
      const remoteDecks = await silentFetch('/decks');
      if (Array.isArray(remoteDecks)) {
        for (const remoteDeck of remoteDecks) {
          const existing = await db.decks.where('remoteId').equals(remoteDeck.deckId).first();
          if (!existing) {
            await db.decks.add({
              name: remoteDeck.name,
              description: remoteDeck.description || '',
              emoji: remoteDeck.emoji || '',
              cardCount: remoteDeck.cardCount || 0,
              isSystem: remoteDeck.isSystem ? 1 : 0,
              systemDeckKey: remoteDeck.systemDeckKey || '',
              createdAt: remoteDeck.createdAt,
              remoteId: remoteDeck.deckId,
              dirty: 0,
              updatedAt: remoteDeck.updatedAt,
              syncedAt: new Date().toISOString(),
            });
          }
        }

        // Pull remote cards for each deck with a remoteId
        const localDecks = await db.decks.where('remoteId').notEqual('').toArray();
        for (const deck of localDecks) {
          if (!deck.remoteId) continue;
          const remoteCards = await silentFetch(`/decks/${deck.remoteId}/cards`);
          if (!Array.isArray(remoteCards)) continue;
          for (const remoteCard of remoteCards) {
            const existingCard = await db.cards.where('remoteId').equals(remoteCard.cardId).first();
            if (!existingCard) {
              await db.cards.add({
                deckId: deck.id,
                front: remoteCard.front,
                back: remoteCard.back,
                fsrs: remoteCard.fsrs || null,
                createdAt: remoteCard.createdAt,
                remoteId: remoteCard.cardId,
                remoteDeckId: deck.remoteId,
                dirty: 0,
                updatedAt: remoteCard.updatedAt,
                syncedAt: new Date().toISOString(),
              });
            }
          }
        }
      }

      // Push any local dirty records
      await this.pushDirtyDecks();
      await this.pushDirtyCards();
      await this.pushDirtyReviews();
      await this.pushDirtyStats();

      this.setStatus('idle');
    } catch (err) {
      console.error('Initial sync failed:', err);
      this.setStatus('error');
    }
  },

  async pushDirtyDecks() {
    const dirtyDecks = await db.decks.where('dirty').equals(1).toArray();
    for (const deck of dirtyDecks) {
      if (deck.remoteId) {
        const ok = await silentFetch(`/decks/${deck.remoteId}`, 'PUT', {
          name: deck.name,
          description: deck.description,
          emoji: deck.emoji,
          cardCount: deck.cardCount,
        });
        if (ok === null) continue;
      } else {
        const remote = await silentFetch('/decks', 'POST', {
          name: deck.name,
          description: deck.description,
          emoji: deck.emoji,
          isSystem: !!deck.isSystem,
          systemDeckKey: deck.systemDeckKey || '',
        });
        if (!remote?.deckId) continue;
        await db.decks.update(deck.id, { remoteId: remote.deckId });
      }
      await db.decks.update(deck.id, { dirty: 0, syncedAt: new Date().toISOString() });
    }
  },

  async pushDirtyCards() {
    const dirtyCards = await db.cards.where('dirty').equals(1).toArray();
    for (const card of dirtyCards) {
      const deck = await db.decks.get(card.deckId);
      const remoteDeckId = deck?.remoteId || card.remoteDeckId;
      if (!remoteDeckId) continue;

      if (card.remoteId) {
        const ok = await silentFetch(
          `/cards/${card.remoteId}?deckId=${remoteDeckId}`,
          'PUT',
          { front: card.front, back: card.back }
        );
        if (ok === null) continue;
      } else {
        const remote = await silentFetch(`/decks/${remoteDeckId}/cards`, 'POST', {
          front: card.front,
          back: card.back,
        });
        if (!remote?.cardId) continue;
        await db.cards.update(card.id, {
          remoteId: remote.cardId,
          remoteDeckId,
        });
      }
      await db.cards.update(card.id, { dirty: 0, syncedAt: new Date().toISOString() });
    }
  },

  async pushDirtyReviews() {
    const dirtyCards = await db.cards.where('dirty').equals(1).toArray();
    const reviewsToSync = [];

    for (const card of dirtyCards) {
      if (!card.remoteId || !card.fsrs) continue;
      const deck = await db.decks.get(card.deckId);
      const remoteDeckId = deck?.remoteId || card.remoteDeckId;
      if (!remoteDeckId) continue;

      reviewsToSync.push({
        deckId: remoteDeckId,
        cardId: card.remoteId,
        state: card.fsrs.state,
        stability: card.fsrs.stability,
        difficulty: card.fsrs.difficulty,
        elapsedDays: card.fsrs.elapsedDays,
        scheduledDays: card.fsrs.scheduledDays,
        reps: card.fsrs.reps,
        lapses: card.fsrs.lapses,
        nextReviewAt: card.fsrs.nextReviewAt,
        lastReviewAt: card.fsrs.lastReviewAt,
      });
    }

    if (reviewsToSync.length > 0) {
      await silentFetch('/sync/reviews', 'POST', { reviews: reviewsToSync });
    }
  },

  async pushDirtyStats() {
    const allStats = await db.stats.toArray();
    for (const stat of allStats) {
      if (!stat.dirty) continue;
      const ok = await silentFetch('/stats', 'POST', {
        date: stat.date,
        cardsStudied: stat.cardsStudied,
        minutesStudied: stat.minutesStudied,
        correctCards: stat.correctCards,
        sessions: stat.sessions,
      });
      if (ok === null) continue;
      await db.stats.update(stat.date, { dirty: 0, syncedAt: new Date().toISOString() });
    }
  },
};
